import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import ButtonOne from '../../common/normal-button';
import {
  CTWA_MANAGE, CTWA_MANAGE_AD, CTWA_MANAGE_ADSET, CTWA_MANAGE_ADSET_ADS, CTWA_MANAGE_ADSET_DUPLICATE, CTWA_MANAGE_STATUS,
} from '../../config/urls';
import { ColorsConstants } from '../../cssUtils/constants';
import { needsUpload, uploadAdMedia, useCtwaApi } from './create/api';
import { AUTOFILL_MAX, emptyAd, initialForm } from './create/constants';
import { adErrors, buildPayload } from './create/validation';
import { AdEditor } from './create/AdsSection';
import { ChatbotSelect, useChatbots } from './create/ChatSections';
import TargetingSection from './create/TargetingSection';
import { Note, currencySymbol } from './create/ui';

// Backend status → chip colours. `status_label` / `reason` come from the backend in plain language.
const TONE = {
  ACTIVE: ['#DCFCE7', '#166534'],
  PAUSED: ['#FEF9C3', '#854D0E'],
  CAMPAIGN_PAUSED: ['#FEF9C3', '#854D0E'],
  ADSET_PAUSED: ['#FEF9C3', '#854D0E'],
  PENDING_REVIEW: ['#DBEAFE', '#1E40AF'],
  IN_PROCESS: ['#DBEAFE', '#1E40AF'],
  DISAPPROVED: ['#FEE2E2', '#991B1B'],
  WITH_ISSUES: ['#FEE2E2', '#991B1B'],
};

const StatusChip = ({ obj }) => {
  const [bg, fg] = TONE[obj.effective_status] || ['#F3F4F6', '#374151'];
  return <Chip size="small" label={obj.status_label || obj.effective_status} sx={{ backgroundColor: bg, color: fg, fontWeight: 700 }} />;
};

const money = (sym, v) => (v == null ? '—' : `${sym}${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));

// Add-ad and edit-&-resubmit share this dialog: AdEditor + the ad's autofill message.
const AdDialog = ({ title, initial, adAccountId, onClose, onSave }) => {
  const api = useCtwaApi();
  const bots = useChatbots();
  const [ad, setAd] = useState(initial);
  const [shown, setShown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const errors = [...adErrors(ad, 'This ad'), ...(ad.autofill.trim() ? [] : ['This ad: add the autofill message.'])];

  const save = async () => {
    setShown(true);
    if (errors.length) return;
    setSaving(true);
    setError('');
    try {
      const mediaAssetId = needsUpload(ad) ? await uploadAdMedia(api, adAccountId, ad) : ad.mediaAssetId;
      const form = { ...initialForm(), autofillMode: 'per_ad', chatbotMode: 'per_ad', ads: [{ ...ad, mediaAssetId }] };
      await onSave(buildPayload(form).ads[0]);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <AdEditor ad={ad} onChange={(p) => setAd((a) => ({ ...a, ...p }))} />
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" fontWeight={700}>Autofill message</Typography>
          <TextField fullWidth size="small" value={ad.autofill} onChange={(e) => setAd((a) => ({ ...a, autofill: e.target.value.slice(0, AUTOFILL_MAX) }))} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <ChatbotSelect bots={bots} value={ad.chatbot} onChange={(chatbot) => setAd((a) => ({ ...a, chatbot }))} label="Chatbot (optional)" />
        </Box>
        {shown && errors.length > 0 && <Alert severity="error" sx={{ mt: 2 }}>{errors.map((e) => <div key={e}>{e}</div>)}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <ButtonOne title={saving ? 'Submitting…' : 'Submit to Meta'} onClick={save} disabled={saving} />
      </DialogActions>
    </Dialog>
  );
};

const TargetingDialog = ({ adset, specialAdCategory, onClose, onSave }) => {
  const [targeting, setTargeting] = useState(adset.targeting_form || initialForm().targeting);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!targeting.locations.length) { setError('Add at least one location.'); return; }
    setSaving(true);
    try {
      await onSave(buildPayload({ ...initialForm(), targeting, specialAdCategory }).targeting);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit targeting · {adset.name}</DialogTitle>
      <DialogContent dividers>
        <TargetingSection targeting={targeting} specialAdCategory={specialAdCategory} onChange={(p) => setTargeting((t) => ({ ...t, ...p }))} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <ButtonOne title={saving ? 'Saving…' : 'Save targeting'} onClick={save} disabled={saving} />
      </DialogActions>
    </Dialog>
  );
};

const DuplicateDialog = ({ adset, onClose, onConfirm }) => {
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState('');
  const confirm = async () => {
    setCopying(true);
    setError('');
    try {
      await onConfirm();
    } catch (e) {
      setError(e.message);
      setCopying(false);
    }
  };
  return (
    <Dialog open onClose={copying ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Duplicate ad set</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">
          Make a copy of <strong>{adset.name}</strong> with its {adset.ads.length} {adset.ads.length === 1 ? 'ad' : 'ads'}?
        </Typography>
        <Note sx={{ mt: 1 }}>The copy starts paused, so it won't spend until you resume it. Copied ads go through Meta review again.</Note>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={copying}>Cancel</Button>
        <ButtonOne title={copying ? 'Copying…' : 'Duplicate'} onClick={confirm} disabled={copying}
          startIcon={copying && <CircularProgress size={16} color="inherit" />} />
      </DialogActions>
    </Dialog>
  );
};

const AdSetCard = ({ adset, sym, canPause, specialAdCategory, adAccountId, onAction }) => {
  const [budget, setBudget] = useState(adset.daily_budget ?? '');
  const [endDate, setEndDate] = useState(adset.end_time ? adset.end_time.slice(0, 10) : '');
  const [dialog, setDialog] = useState(null); // { kind: 'targeting' } | { kind: 'add' } | { kind: 'edit', ad }
  const paused = adset.status === 'PAUSED';
  const run = (...args) => onAction(...args).catch(() => {}); // the page banner shows the error

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
        <Typography variant="body1" fontWeight={700}>Ad set · {adset.name}</Typography>
        <StatusChip obj={adset} />
        {adset.reason && <Note>{adset.reason}</Note>}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          {canPause && (
            <Button size="small" variant="outlined" onClick={() => run(CTWA_MANAGE_STATUS(adset.id), { status: paused ? 'ACTIVE' : 'PAUSED' })}>
              {paused ? 'Resume' : 'Pause'}
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={() => setDialog({ kind: 'duplicate' })}>
            Duplicate
          </Button>
        </Box>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={3} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>Daily budget</Typography>
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} sx={{ width: 130 }}
              InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5 }}>{sym.trim()}</Box> }} inputProps={{ 'aria-label': 'Daily budget' }} />
            <Button size="small" variant="contained" disableElevation disabled={!Number(budget) || Number(budget) === Number(adset.daily_budget)}
              onClick={() => run(CTWA_MANAGE_ADSET(adset.id), { daily_budget: Number(budget) })}>Save</Button>
          </Stack>
          <Note sx={{ mt: 0.5 }}>Was {money(sym, adset.daily_budget)}. Changes apply within minutes.</Note>
        </Box>
        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>End date</Typography>
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} inputProps={{ 'aria-label': 'End date', min: new Date().toISOString().split('T')[0] }} />
            <Button size="small" variant="contained" disableElevation disabled={!endDate} onClick={() => run(CTWA_MANAGE_ADSET(adset.id), { end_date: endDate })}>
              {adset.end_time ? 'Extend' : 'Set'}
            </Button>
            {adset.end_time && <Button size="small" onClick={() => run(CTWA_MANAGE_ADSET(adset.id), { end_date: null })}>Remove</Button>}
          </Stack>
          <Note sx={{ mt: 0.5 }}>{adset.end_time ? `Ends ${adset.end_time.slice(0, 10)}` : 'Runs until you pause it'}</Note>
        </Box>
        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>Audience</Typography>
          <Typography variant="body2">{adset.audience_summary || '—'}</Typography>
          <Button size="small" sx={{ px: 0, textTransform: 'none' }} disabled={!adset.targeting_form} onClick={() => setDialog({ kind: 'targeting' })}>Edit targeting</Button>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight={700}>Ads</Typography>
        <Button size="small" onClick={() => setDialog({ kind: 'add' })} disabled={adset.ads.length >= 5}>+ Add ad</Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Ad', 'Chatbot', 'Status', 'Conversations', 'Cost / conv.', 'Qualified leads', 'Action'].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {adset.ads.map((ad) => (
              <TableRow key={ad.id}>
                <TableCell>{ad.name}</TableCell>
                <TableCell>{ad.chatbot ? `${ad.chatbot.name} (${ad.chatbot.type === 'nodebot' ? 'workflow' : 'custom'})` : '—'}</TableCell>
                <TableCell>
                  <StatusChip obj={ad} />
                  {ad.reason && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: ad.rejected ? '#991B1B' : ColorsConstants.primaryGray50 }}>{ad.reason}</Typography>}
                </TableCell>
                <TableCell>{num(ad.metrics?.conversations)}</TableCell>
                <TableCell>{money(sym, ad.metrics?.cost_per_conversation)}</TableCell>
                <TableCell>{num(ad.metrics?.qualified_leads)}</TableCell>
                <TableCell>
                  {ad.rejected ? (
                    <Button size="small" color="error" disabled={!ad.ad_form} onClick={() => setDialog({ kind: 'edit', ad })}>Edit & resubmit</Button>
                  ) : canPause && (
                    <Button size="small" onClick={() => run(CTWA_MANAGE_STATUS(ad.id), { status: ad.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED' })}>
                      {ad.status === 'PAUSED' ? 'Resume' : 'Pause'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {dialog?.kind === 'duplicate' && (
        <DuplicateDialog adset={adset} onClose={() => setDialog(null)}
          onConfirm={() => onAction(CTWA_MANAGE_ADSET_DUPLICATE(adset.id), {}).then(() => setDialog(null))} />
      )}
      {dialog?.kind === 'targeting' && (
        <TargetingDialog adset={adset} specialAdCategory={specialAdCategory} onClose={() => setDialog(null)}
          onSave={(targeting) => onAction(CTWA_MANAGE_ADSET(adset.id), { targeting }).then(() => setDialog(null))} />
      )}
      {dialog?.kind === 'add' && (
        <AdDialog title="Add an ad" initial={emptyAd(adset.ads.length + 1)} adAccountId={adAccountId} onClose={() => setDialog(null)}
          onSave={(ad) => onAction(CTWA_MANAGE_ADSET_ADS(adset.id), { ad }).then(() => setDialog(null))} />
      )}
      {dialog?.kind === 'edit' && (
        <AdDialog title={`Edit & resubmit · ${dialog.ad.name}`} initial={{ ...emptyAd(), ...dialog.ad.ad_form }} adAccountId={adAccountId} onClose={() => setDialog(null)}
          onSave={(ad) => onAction(CTWA_MANAGE_AD(dialog.ad.id), { ad }).then(() => setDialog(null))} />
      )}
    </Paper>
  );
};

/**
 * Screen 6 — Manage a published campaign. Loads live from Meta every time it opens (no background sync).
 */
const CTWAManage = ({ campaignId, onBack, canPause = true }) => {
  const api = useCtwaApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try { setData(await api.get(CTWA_MANAGE(campaignId))); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  // Every action posts, then reloads so statuses come back from Meta. Rejects so dialogs can show the error.
  const onAction = async (url, body) => {
    setActionError('');
    try {
      await api.post(url, body);
      await load();
    } catch (e) {
      setActionError(e.message);
      throw e;
    }
  };
  const act = (...args) => onAction(...args).catch(() => {});

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  const sym = currencySymbol(data?.currency || 'INR');
  const c = data?.campaign;
  const m = data?.metrics || {};
  const rejectedCount = (data?.adsets || []).flatMap((s) => s.ads).filter((a) => a.rejected).length;
  const stats = [
    ['Spend', money(sym, m.spend), 'Meta'],
    ['Reach', num(m.reach), 'Meta'],
    ['Conversations started', num(m.conversations), 'Meta'],
    ['Cost per conversation', money(sym, m.cost_per_conversation), 'Meta'],
    ['Qualified leads', num(m.qualified_leads), 'BotBridge'],
    ['Cost per qualified lead', money(sym, m.cost_per_qualified_lead), 'BotBridge + Meta'],
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: 'none', color: ColorsConstants.primaryGray60 }}>Campaigns</Button>
        {c && <Typography variant="h6" fontWeight={700}>{c.name}</Typography>}
        {c && <StatusChip obj={c} />}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {data?.synced_at && <Note>Loaded from Meta at {new Date(data.synced_at).toLocaleTimeString()}</Note>}
          <Button size="small" startIcon={loading ? <CircularProgress size={14} /> : <Refresh />} onClick={load} disabled={loading}>Refresh</Button>
          {c && canPause && (
            <ButtonOne title={c.status === 'PAUSED' ? 'Resume campaign' : 'Pause campaign'} variant="outlined"
              onClick={() => act(CTWA_MANAGE_STATUS(c.id), { status: c.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED' })} />
          )}
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={load}>Retry</Button>}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}
      {c?.reason && <Alert severity="info" sx={{ mb: 2 }}>{c.reason}</Alert>}
      {rejectedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {rejectedCount} {rejectedCount === 1 ? 'ad was' : 'ads were'} rejected by Meta. See the reason next to the ad and use "Edit & resubmit".
        </Alert>
      )}

      {data && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5, mb: 2.5 }}>
            {stats.map(([label, value, src]) => (
              <Paper key={label} elevation={0} sx={{ p: 2, border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2 }}>
                <Typography variant="caption" color={ColorsConstants.primaryGray50}>{label}</Typography>
                <Typography variant="h6" fontWeight={700}>{value}</Typography>
                <Note>{src}</Note>
              </Paper>
            ))}
          </Box>
          <Stack spacing={2}>
            {data.adsets.map((s) => (
              <AdSetCard key={s.id} adset={s} sym={sym} canPause={canPause} specialAdCategory={data.special_ad_category}
                adAccountId={data.ad_account_id} onAction={onAction} />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default CTWAManage;
